/**
 * Conversions between the `YYYY-MM-DD` strings our forms and Postgres `date`
 * columns use, and the `Date` objects react-day-picker works in.
 *
 * These exist because the obvious code is wrong. `new Date('2026-08-03')`
 * is specified to parse a date-only string as **UTC** midnight, so anywhere
 * west of UTC it renders as 2 August — a helper in a UTC-5 market picking
 * their birthday would see it shift by a day. Conversely `toISOString()`
 * on a local midnight Date shifts forward east of UTC.
 *
 * So: parse by constructing a LOCAL date from the parts, and format by
 * reading the LOCAL parts back. Never round-trip a date-only value through
 * `Date.parse` or `toISOString`.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → local-midnight Date. Returns undefined for empty/invalid. */
export function parseISODate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const m = ISO_DATE.exec(value.trim());
  if (!m) return undefined;
  const [, y, mo, d] = m;
  const year = Number(y), month = Number(mo), day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const date = new Date(year, month - 1, day);
  // Rejects real-world impossibilities like 2026-02-31, which the Date
  // constructor would silently roll forward to 3 March.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

/** Date → `YYYY-MM-DD` using LOCAL parts (never toISOString). */
export function formatISODate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Human-readable label for the trigger, in the user's locale. */
export function formatDisplayDate(
  value: string | null | undefined,
  locale?: string,
): string {
  const date = parseISODate(value);
  if (!date) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * `YYYY-MM-DD` for a date exactly `years` before today, in LOCAL time.
 *
 * Replaces the `new Date(...setFullYear(...)).toISOString().split('T')[0]`
 * idiom that was inline in the onboarding forms: `toISOString()` converts to
 * UTC first, so east of UTC that expression yields tomorrow's date — an
 * 18-year age gate that is off by a day for part of the world, every day.
 */
export function isoDateYearsAgo(years: number): string {
  const now = new Date();
  return formatISODate(
    new Date(now.getFullYear() - years, now.getMonth(), now.getDate()),
  );
}

/**
 * A full timestamp (Postgres `timestamptz`) -> Date. Undefined when absent or
 * unparseable.
 *
 * Distinct from parseISODate, and deliberately so. parseISODate accepts ONLY
 * `YYYY-MM-DD` and builds a local-midnight Date, which is correct for `date`
 * columns and wrong for anything carrying a time and an offset — it returns
 * undefined for `2026-08-22T20:55:09.021+00:00`. Callers that paired that
 * undefined with a `?? new Date()` fallback silently rendered the current
 * time in place of the real one, which is at its worst on the panels that
 * exist to record exactly when a provider confirmed something.
 *
 * Here `new Date(value)` IS right: a timestamp with an explicit offset has
 * one unambiguous instant, so there is no local-vs-UTC trap to avoid.
 */
export function parseTimestamp(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
